/**
 * @fileoverview PlannerPuffin Terms of Service registry data.
 *
 * Hand-migrated faithfully from
 * `apps/planner-web/src/portals/marketing-website/pages/TermsOfServicePage.jsx`.
 */

/** @type {import('../../types.js').LegalDocument} */
export const terms = Object.freeze({
  appKey: 'planner',
  documentKey: 'terms',
  frontmatter: {
    title: 'Terms of Service',
    productName: 'PlannerPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-03-01',
    contactEmail: 'legal@plannerpuffin.com',
    canonicalUrl: 'https://plannerpuffin.com/terms',
    ogImage: 'https://plannerpuffin.com/og-image.jpg',
    description:
      'PlannerPuffin terms of service. Review the terms governing use of our workforce optimization platform.',
  },
  sections: [
    {
      id: 'agreement-to-terms',
      title: '1. Agreement to Terms',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'By accessing or using {productName}, you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use our services. These terms apply to all users, including businesses and individuals who access {productName} for workforce optimization and labor planning.',
        },
      ],
    },
    {
      id: 'description-of-service',
      title: '2. Description of Service',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides workforce optimization for retail operations. Our platform enables sales-driven scheduling, labor budgeting, SPLH tracking, four-wall P&L analytics, and approval workflow management. We help retail operators align labor costs with sales demand across single and multi-location operations.',
        },
      ],
    },
    {
      id: 'account-registration',
      title: '3. Account Registration',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorized access.',
        },
      ],
    },
    {
      id: 'subscription-and-billing',
      title: '4. Subscription and Billing',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} is offered on a subscription basis. Contact us for pricing details. Plans are based on the number of locations and features needed. You may cancel your subscription at any time. No refunds are provided for partial billing periods.',
        },
      ],
    },
    {
      id: 'acceptable-use',
      title: '5. Acceptable Use',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'You agree to use {productName} only for lawful purposes and in accordance with these terms. You may not use our services to submit false scheduling data, misrepresent labor figures, or engage in any activity that violates applicable labor laws or regulations.',
        },
      ],
    },
    {
      id: 'intellectual-property',
      title: '6. Intellectual Property',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'All content, features, and functionality of {productName}, including our scheduling algorithms, analytics dashboards, and documentation, are owned by {productName} and protected by intellectual property laws. You may not copy, modify, or distribute our proprietary technology.',
        },
      ],
    },
    {
      id: 'data-and-privacy',
      title: '7. Data and Privacy',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'Your use of {productName} is also governed by our Privacy Policy, which describes how we collect, use, and protect your data. By using our services, you consent to the data practices described in our Privacy Policy.',
        },
      ],
    },
    {
      id: 'limitation-of-liability',
      title: '8. Limitation of Liability',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides scheduling recommendations and labor analytics, but does not guarantee specific cost savings or profitability outcomes. Labor planning decisions remain the responsibility of your management team. Our liability is limited to the amount you paid for the service in the preceding 12 months.',
        },
      ],
    },
    {
      id: 'termination',
      title: '9. Termination',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may suspend or terminate your access to {productName} if you violate these terms. You may cancel your account at any time. Upon termination, your right to use the service ceases immediately, though we will retain data as described in our Privacy Policy.',
        },
      ],
    },
    {
      id: 'changes-to-terms',
      title: '10. Changes to Terms',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'We reserve the right to modify these terms at any time. We will provide notice of material changes via email or through our platform. Continued use of {productName} after changes take effect constitutes acceptance of the revised terms.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '11. Contact Us',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about these Terms of Service, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
